import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { CalendarDays, Home, LogOut, User, Mail, Lock, UserPlus, X, Chrome } from 'lucide-react';
import { useAppStore } from '../../stores/wpAppStore';
import Avatar from './Avatar';
import { wpApi } from '../../services/wordpressApi';
import { 
  signInWithEmail, 
  signUpWithEmail, 
  signInWithGoogle, 
  signInWithGoogleIdToken,
  signOut as firebaseSignOut,
  onAuthChange,
  type AuthUser 
} from '../../services/firebaseAuth';
import { useState, useEffect, useCallback } from 'react';
import { isNativeApp } from '../../lib/isNativeApp';

// Điền mật khẩu mặc định cho tài khoản test ở đây (Application Password từ WP Admin)
const DEFAULT_USERNAME = 'testuser';
const DEFAULT_PASSWORD = '';

const tabs = [
  { path: '/', label: 'Feed', icon: Home },
  { path: '/tournaments', label: 'Giải đấu', icon: CalendarDays },
  { path: '/profile', label: 'Cá nhân', icon: User },
];

const styles = {
  header: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    background: 'rgba(24, 24, 27, 0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid #27272a',
    zIndex: 100,
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textDecoration: 'none',
    color: 'inherit',
  },
  logo: {
    width: '40px',
    height: '40px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  brandName: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fafafa',
  },
  brandTagline: {
    fontSize: '11px',
    color: '#71717a',
    marginTop: '-2px',
  },
  desktopNav: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    textDecoration: 'none',
    color: '#a1a1aa',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  navLinkActive: {
    background: '#27272a',
    color: '#fafafa',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 12px 6px 6px',
    background: '#27272a',
    borderRadius: '9999px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#fafafa',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    color: '#71717a',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#fafafa',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#71717a',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '8px',
    display: 'flex',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    background: '#27272a',
    borderRadius: '12px',
    padding: '4px',
  },
  tab: {
    flex: 1,
    padding: '10px',
    background: 'none',
    border: 'none',
    borderRadius: '10px',
    color: '#a1a1aa',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
    position: 'relative' as const,
    zIndex: 0,
    overflow: 'hidden',
  },
  tabActive: {
    background: '#3b82f6',
    color: '#fff',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fafafa',
  },
  inputWrapper: {
    position: 'relative' as const,
  },
  inputIcon: {
    position: 'absolute' as const,
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#71717a',
  },
  input: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '12px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  },
  hint: {
    fontSize: '12px',
    color: '#71717a',
    marginTop: '4px',
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '12px',
    background: '#fff',
    border: 'none',
    borderRadius: '12px',
    color: '#18181b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '16px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '20px 0',
    color: '#71717a',
    fontSize: '13px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#27272a',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px',
    background: '#27272a',
    color: '#a1a1aa',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    flex: 1,
    padding: '12px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
  error: {
    padding: '12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '10px',
    color: '#ef4444',
    fontSize: '13px',
  },
  success: {
    padding: '12px',
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: '10px',
    color: '#22c55e',
    fontSize: '13px',
    textAlign: 'center' as const,
  },
};

export default function Header() {
  const currentUser = useAppStore((state) => state.currentUser);
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const logout = useAppStore((state) => state.logout);

  // Trong app native: ẩn thanh header + bottom-nav của web (app có thanh riêng)
  const isNative = isNativeApp();
  
  const [showLogin, setShowLogin] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loginForm, setLoginForm] = useState(() => ({
    email: localStorage.getItem('fb_email') || '',
    password: '',
  }));
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', confirmPassword: '', displayName: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        try {
          const result = await wpApi.linkFirebaseUser(
            user.uid,
            user.email || '',
            user.displayName || '',
            user.photoURL || ''
          );
          if (result.success && result.user) {
            setCurrentUser(result.user);
            wpApi.persistFirebaseAuthState(user.uid, user.email || '');
          }
        } catch (err) {
          console.error('Failed to link Firebase user:', err);
        }
      } else {
        logout();
      }
    });
    return () => unsubscribe();
  }, [setCurrentUser, logout]);

  useEffect(() => {
    const handleNativeAuth = async (event: Event) => {
      const detail = (event as CustomEvent<{
        googleIdToken?: string;
        firebaseIdToken?: string;
        firebaseUid?: string;
        email?: string;
        displayName?: string;
        photoURL?: string;
      }>).detail;
      if (!detail) return;
      try {
        // Ưu tiên: app đưa thẳng Firebase ID token + UID -> web dùng luôn, không cần Google
        if (detail.firebaseIdToken && detail.firebaseUid) {
          wpApi.setInjectedFirebaseToken(detail.firebaseIdToken);
          const result = await wpApi.linkFirebaseUser(
            detail.firebaseUid,
            detail.email || '',
            detail.displayName || '',
            detail.photoURL || '',
            detail.firebaseIdToken,
          );
          if (result.success && result.user) {
            setCurrentUser(result.user);
            wpApi.persistFirebaseAuthState(detail.firebaseUid, result.user.email || '');
          }
          return;
        }
        if (!detail.googleIdToken) return;
        const user = await signInWithGoogleIdToken(detail.googleIdToken);
        const result = await wpApi.linkFirebaseUser(
          user.uid,
          user.email || '',
          user.displayName || '',
          user.photoURL || '',
        );
        if (result.success && result.user) {
          setCurrentUser(result.user);
          wpApi.persistFirebaseAuthState(user.uid, user.email || '');
        }
      } catch (error) {
        console.error('Native auth bridge failed:', error);
      }
    };
    window.addEventListener('vncues:native-auth', handleNativeAuth);
    return () => window.removeEventListener('vncues:native-auth', handleNativeAuth);
  }, [setCurrentUser]);

  // Mở modal đăng nhập khi web yêu cầu (ví dụ: bấm "Đăng nhập để bình luận")
  useEffect(() => {
    const handleOpenLogin = () => {
      setShowLogin(true);
      if (isNative) {
        toast.info('Đăng nhập từ màn hình hồ sơ của app hoặc qua Google trên web');
      }
    };
    window.addEventListener('vncues:open-login-modal', handleOpenLogin);
    return () => window.removeEventListener('vncues:open-login-modal', handleOpenLogin);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const user = await signInWithEmail(loginForm.email, loginForm.password);
      
      // Link Firebase user to WordPress ngay lập tức
      const result = await wpApi.linkFirebaseUser(
        user.uid,
        user.email || '',
        user.displayName || '',
        user.photoURL || ''
      );
      
      if (result.success && result.user) {
        setCurrentUser(result.user);
        wpApi.persistFirebaseAuthState(user.uid, user.email || '');
        toast.success('Đăng nhập thành công!');
        setShowLogin(false);
        setLoginForm({ email: '', password: '' });
      } else {
        setError(result.message || 'Không thể liên kết tài khoản với WordPress');
      }
    } catch (err: any) {
      setError(err.message || 'Đăng nhập thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      setLoading(false);
      return;
    }
    
    if (registerForm.password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự');
      setLoading(false);
      return;
    }
    
    try {
      const user = await signUpWithEmail(registerForm.email, registerForm.password, registerForm.displayName);
      const result = await wpApi.linkFirebaseUser(
        user.uid,
        user.email || '',
        user.displayName || registerForm.displayName,
        user.photoURL || ''
      );
      if (!result.success || !result.user) {
        throw new Error(result.message || 'Không thể liên kết tài khoản với WordPress');
      }
      setCurrentUser(result.user);
      wpApi.persistFirebaseAuthState(user.uid, user.email || '');
      toast.success('Đăng ký thành công!');
      setShowLogin(false);
      setRegisterForm({ email: '', password: '', confirmPassword: '', displayName: '' });
    } catch (err: any) {
      setError(err.message || 'Đăng ký thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Google Identity Services lấy ID token rồi đổi sang Firebase bằng signInWithCredential.
      // Tránh hoàn toàn `__/auth/handler` + `init.json` (404 vì project không có Firebase Hosting).
      const user = await signInWithGoogle();
      
      const result = await wpApi.linkFirebaseUser(
        user.uid,
        user.email || '',
        user.displayName || '',
        user.photoURL || ''
      );
      
      if (result.success && result.user) {
        setCurrentUser(result.user);
        wpApi.persistFirebaseAuthState(user.uid, user.email || '');
        setSuccess('Đăng nhập thành công!');
        setTimeout(() => {
          setShowLogin(false);
          setError('');
        }, 1000);
      } else {
        setError(result.message || 'Không thể liên kết tài khoản với WordPress');
      }
    } catch (err: any) {
      console.error('Google login error:', err);
      let message = err.message || 'Đăng nhập Google thất bại';
      // Xử lý lỗi liên quan popup / Google Identity Services
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        message = 'Pop-up bị chặn. Vui lòng cho phép popup và thử lại.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        message = 'Đăng nhập đã bị hủy. Vui lòng thử lại.';
      } else if (err.code === 'auth/unauthorized-domain') {
        message = 'Miền chưa được cấu hình trên Firebase. Vui lòng thêm ' + window.location.hostname + ' vào Authorized domains trong Firebase Console.';
      } else if (err.code === 'auth/operation-not-supported-in-this-environment') {
        message = 'Đăng nhập Google không khả dụng trên môi trường này. Vui lòng dùng đăng nhập email.';
      } else if (typeof err === 'string' && (err.indexOf('origin_mismatch') >= 0 || err.indexOf('invalid_client') >= 0 || err.indexOf('blocked_iframe') >= 0)) {
        message = 'Google chưa cho phép ' + window.location.origin + '. Vui lòng thêm domain này vào Authorized JavaScript origins của Google Cloud Console (OAuth client 49768798719-lbcg2e1smi4v3voq1fvk0va2g8e7ansm.apps.googleusercontent.com).';
      } else if (typeof err === 'string' && (err.indexOf('canceled') >= 0 || err.indexOf('popup_failed_to_open') >= 0)) {
        message = 'Bạn đã đóng cửa sổ Google. Vui lòng thử lại.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await firebaseSignOut();
      logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleClose = () => {
    setShowLogin(false);
    setError('');
    setSuccess('');
    setLoginForm({
      email: localStorage.getItem('fb_email') || '',
      password: '',
    });
    setRegisterForm({ email: '', password: '', confirmPassword: '', displayName: '' });
  };

  const isActive = (path: string) => typeof window !== 'undefined' && window.location.pathname === path;

  return (
    <>
      {!isNative && (
        <header style={styles.header}>
          <div style={styles.headerInner}>
          <a href="/" style={styles.brand}>
            <motion.div
              style={styles.logo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🎱
            </motion.div>
            <div>
              <div style={styles.brandName}>VnCues</div>
              <div style={styles.brandTagline}>Billiards Community</div>
            </div>
          </a>

          <nav style={styles.desktopNav}>
            {tabs.map(({ path, label, icon: Icon }) => (
              <a
                key={path}
                href={path}
                style={{
                  ...styles.navLink,
                  ...(isActive(path) ? styles.navLinkActive : {}),
                }}
                onMouseEnter={(e) => {
                  if (!isActive(path)) e.currentTarget.style.background = '#27272a';
                }}
                onMouseLeave={(e) => {
                  if (!isActive(path)) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon size={16} />
                {label}
              </a>
            ))}
          </nav>

          {isAuthenticated && currentUser ? (
            <motion.div
              style={styles.userSection}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Avatar
                name={currentUser.displayName || '?'}
                photoURL={currentUser.photoURL}
                size={30}
              />
              <span style={styles.userName}>{currentUser.displayName}</span>
              <button 
                style={styles.logoutBtn}
                onClick={handleLogout}
                title="Đăng xuất"
                onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#71717a'}
              >
                <LogOut size={16} />
              </button>
            </motion.div>
          ) : null}
        </div>
        </header>
      )}

      {/* Login/Register Modal */}
      <AnimatePresence>
        {showLogin && (
          <motion.div
            style={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
          <motion.div
            style={styles.modal}
            onClick={e => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          >
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {activeTab === 'login' ? 'Chào mừng!' : 'Tạo tài khoản'}
              </h2>
              <button style={styles.closeBtn} onClick={handleClose}>
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
              <motion.button
                style={{ ...styles.tab, color: activeTab === 'login' ? '#fff' : '#a1a1aa' }}
                onClick={() => { setActiveTab('login'); setError(''); setSuccess(''); }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Đăng nhập
                {activeTab === 'login' && (
                  <motion.span
                    layoutId="activeTab"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: '#3b82f6',
                      borderRadius: '10px',
                      zIndex: -1,
                    }}
                    transition={{ type: 'spring', damping: 28, stiffness: 400 }}
                  />
                )}
              </motion.button>
              <motion.button
                style={{ ...styles.tab, color: activeTab === 'register' ? '#fff' : '#a1a1aa' }}
                onClick={() => { setActiveTab('register'); setError(''); setSuccess(''); }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Đăng ký
                {activeTab === 'register' && (
                  <motion.span
                    layoutId="activeTab"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: '#3b82f6',
                      borderRadius: '10px',
                      zIndex: -1,
                    }}
                    transition={{ type: 'spring', damping: 28, stiffness: 400 }}
                  />
                )}
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  key="error"
                  style={styles.error}
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {error}
                </motion.div>
              )}
              {!error && success && (
                <motion.div
                  key="success"
                  style={styles.success}
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait" initial={false}>
            {activeTab === 'login' ? (
              <motion.div
                key="login-form"
                style={{ width: '100%' }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Google Login Button */}
                <button
                  style={{
                    ...styles.googleBtn,
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? 'wait' : 'pointer',
                  }}
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#f5f5f5')}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <Chrome size={18} style={{ marginRight: '8px' }} />
                  {loading ? 'Đang xử lý...' : 'Tiếp tục với Google'}
                </button>

                <div style={styles.divider}>
                  <div style={styles.dividerLine} />
                  hoặc
                  <div style={styles.dividerLine} />
                </div>

                <form onSubmit={handleLogin} style={styles.form}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email</label>
                    <div style={styles.inputWrapper}>
                      <Mail size={18} style={styles.inputIcon} />
                      <input
                        type="email"
                        value={loginForm.email}
                        onChange={e => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Nhập email của bạn"
                        required
                        style={styles.input}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#27272a'}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Mật khẩu</label>
                    <div style={styles.inputWrapper}>
                      <Lock size={18} style={styles.inputIcon} />
                      <input
                        type="password"
                        value={loginForm.password}
                        onChange={e => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Nhập mật khẩu"
                        required
                        style={styles.input}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#27272a'}
                      />
                    </div>
                    <small style={styles.hint}>
                      Quên mật khẩu? Liên hệ admin để reset
                    </small>
                  </div>
                  
                  <div style={styles.actions}>
                    <button type="button" style={styles.cancelBtn} onClick={handleClose} disabled={loading}>
                      Hủy
                    </button>
                    <button type="submit" style={styles.submitBtn} disabled={loading}>
                      {loading ? 'Đang xử lý...' : 'Đăng nhập'}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="register-form"
                style={{ width: '100%' }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
              <form onSubmit={handleRegister} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <div style={styles.inputWrapper}>
                    <Mail size={18} style={styles.inputIcon} />
                    <input
                      type="email"
                      value={registerForm.email}
                      onChange={e => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                      required
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#27272a'}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Tên hiển thị</label>
                  <div style={styles.inputWrapper}>
                    <User size={18} style={styles.inputIcon} />
                    <input
                      type="text"
                      value={registerForm.displayName}
                      onChange={e => setRegisterForm(prev => ({ ...prev, displayName: e.target.value }))}
                      placeholder="Tên bạn trên VNCues"
                      required
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#27272a'}
                    />
                  </div>
                </div>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Mật khẩu</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={18} style={styles.inputIcon} />
                    <input
                      type="password"
                      value={registerForm.password}
                      onChange={e => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Ít nhất 6 ký tự"
                      required
                      minLength={6}
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#27272a'}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Xác nhận mật khẩu</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={18} style={styles.inputIcon} />
                    <input
                      type="password"
                      value={registerForm.confirmPassword}
                      onChange={e => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Nhập lại mật khẩu"
                      required
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#27272a'}
                    />
                  </div>
                </div>
                
                <div style={styles.actions}>
                  <button type="button" style={styles.cancelBtn} onClick={handleClose} disabled={loading}>
                    Hủy
                  </button>
                  <button type="submit" style={styles.submitBtn} disabled={loading}>
                    <UserPlus size={16} style={{ marginRight: '6px' }} />
                    {loading ? 'Đang xử lý...' : 'Đăng ký'}
                  </button>
                </div>
              </form>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
